import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllCoursesStatic } from './cours/courses-data';
import { courseStructure as baseprogrammationStructure } from './cours/baseprogrammation/course-structure';
import { courseStructure as amelioreviséeStructure } from './cours/ameliorevisée/course-structure';
import { courseStructure as morpionStructure } from './cours/morpion/course-structure';
import { courseStructure as flappybirdStructure } from './cours/flappybird/course-structure';
import { courseStructure as snakeStructure } from './cours/snake/course-structure';
import { usePassCodeur } from '../lib/pass-codeur-context';

// Import des composants UI
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Trophy } from 'lucide-react';
import SEO from '../components/SEO';

const STORAGE_KEY = 'courseProgress';

// Map des structures de cours
const courseStructures = {
  baseprogrammation: baseprogrammationStructure,
  ameliorevisée: amelioreviséeStructure,
  morpion: morpionStructure,
  flappybird: flappybirdStructure,
  snake: snakeStructure,
};

// Fonction pour obtenir le nombre de chapitres d'un cours
const getChapterCount = (courseId) => {
  const structure = courseStructures[courseId];
  if (!structure || !structure.chapters) return 0;
  
  // Inclure tous les chapitres dans le calcul, y compris l'introduction
  return structure.chapters.length;
};

// Fonction pour obtenir le prochain chapitre à apprendre
const getNextChapterToLearn = (courseId) => {
  try {
    const progress = localStorage.getItem(STORAGE_KEY);
    if (!progress) return '/cours/' + courseId; // Si pas de progression, commencer au début
    
    const progressData = JSON.parse(progress);
    const lastCompletedChapter = progressData[courseId];
    
    // Si le cours n'a pas été commencé du tout
    if (lastCompletedChapter === undefined) {
      return '/cours/' + courseId; // Rediriger vers l'introduction
    }
    
    // Obtenir la structure du cours
    const structure = courseStructures[courseId];
    if (!structure || !structure.chapters) return '/cours/' + courseId;
    
    // Si l'utilisateur a complété l'introduction (lastCompletedChapter === 0)
    // ou un autre chapitre, on le dirige vers le prochain chapitre
    const nextChapterNumber = lastCompletedChapter + 1;
    
    // Trouver l'ID du prochain chapitre
    let nextChapterId;
    if (nextChapterNumber === 1) {
      nextChapterId = 'chapitre1'; // Après l'intro, aller au chapitre 1
    } else if (nextChapterNumber > 1) {
      nextChapterId = `chapitre${nextChapterNumber}`; // Chapitre suivant
    }
    
    // Trouver le chapitre correspondant
    const nextChapter = structure.chapters.find(
      chapter => chapter.id === nextChapterId
    );
    
    // Si on trouve le prochain chapitre, retourner son chemin
    if (nextChapter) {
      return nextChapter.path;
    }
    
    // Si pas de prochain chapitre (cours terminé), retourner au début du cours
    return '/cours/' + courseId;
  } catch (error) {
    console.error('Erreur lors de la recherche du prochain chapitre:', error);
    return '/cours/' + courseId;
  }
};

// Fonction pour vérifier si un cours est terminé
const isCourseCompleted = (courseId) => {
  try {
    const progress = localStorage.getItem(STORAGE_KEY);
    if (!progress) return false;
    
    const progressData = JSON.parse(progress);
    const courseProgress = progressData[courseId];
    
    // Obtenir le nombre de chapitres pour ce cours
    const chapterCount = getChapterCount(courseId);
    if (chapterCount === 0) return false;
    
    // Le cours est terminé si courseProgress correspond au dernier chapitre (chapitre le plus élevé)
    return courseProgress >= chapterCount - 1;
    
  } catch (error) {
    console.error('Erreur lors de la lecture de la progression:', error);
    return false;
  }
};

// Fonction pour obtenir le pourcentage de progression d'un cours
const getCourseProgress = (courseId) => {
  try {
    const progress = localStorage.getItem(STORAGE_KEY);
    if (!progress) return 0;
    
    const progressData = JSON.parse(progress);
    const courseProgress = progressData[courseId];
    
    const chapterCount = getChapterCount(courseId);
    // Si pas de chapitres, retourner 0%
    if (!chapterCount) return 0;
    
    // L'introduction (chapitre 0) est comptée comme chapitre 1
    // Si courseProgress est 0, l'utilisateur a terminé l'introduction (1 chapitre sur le total)
    // Si courseProgress est -1, l'utilisateur n'a pas encore commencé (0 chapitre sur le total)
    const completedChapters = courseProgress + 1; // +1 car on compte à partir de 0
    const percentage = Math.min(Math.round((completedChapters / chapterCount) * 100), 100);
    
    // Vérifier que le pourcentage est un nombre valide entre 0 et 100
    return Number.isFinite(percentage) ? percentage : 0;
  } catch (error) {
    console.error('Erreur lors de la lecture de la progression:', error);
    return 0;
  }
};

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { hasPassCodeur } = usePassCodeur();

  // Scroll vers le haut de la page au chargement du composant
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = () => {
      try {
        // Utiliser les données statiques au lieu de Firebase
        const allCourses = getAllCoursesStatic();
        setCourses(allCourses);
      } catch (error) {
        console.error("Erreur lors de la récupération des cours:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const renderCourseCard = (course) => {
    const isCompleted = isCourseCompleted(course.id);
    const progress = getCourseProgress(course.id);
    const isFree = course.isFree;
    
    return (
      <Card 
        key={course.id} 
        className="overflow-hidden flex flex-col cursor-pointer hover:shadow-lg transition-shadow duration-200"
      >
        <a 
          href={course.courseUrl || `/cours/${course.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-grow"
          onClick={(e) => {
            if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
              e.preventDefault();
              const nextChapterPath = getNextChapterToLearn(course.id);
              navigate(nextChapterPath);
            }
          }}
        >
          <div className="h-48 bg-muted relative">
            <img 
              src={courseStructures[course.id]?.image}
              alt={course.title} 
              className="w-full h-full object-cover"
            />
            {isCompleted && (
              <div className="absolute -top-3 -right-3 bg-green-500 text-white p-4 rounded-full shadow-lg transform hover:scale-110 transition-transform duration-200 z-10">
                <Trophy className="h-8 w-8 animate-pulse" />
              </div>
            )}

          </div>
          <CardHeader>
            <div className="flex justify-between items-start">
              <CardTitle>{course.title}</CardTitle>
              <div className="flex gap-2">
                {isCompleted && (
                  <Badge className="bg-green-500 hover:bg-green-600 transition-colors whitespace-nowrap px-2" variant="success">
                    <Trophy className="h-4 w-4 mr-1" />
                    Terminé !
                  </Badge>
                )}
                <Badge variant="outline">
                  {course.level || 'Tous niveaux'}
                </Badge>
              </div>
            </div>
            <CardDescription>{course.description}</CardDescription>
          </CardHeader>
          <CardContent className="flex-grow">
            <ul className="space-y-1 text-sm">
              {course.highlights && course.highlights.map((highlight, index) => (
                <li key={index} className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  {highlight}
                </li>
              ))}
            </ul>
          </CardContent>
          <div className="px-6 pb-4">
            <div className="text-sm text-muted-foreground mb-1">
              Progression: {isCompleted ? '100' : progress}%
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-300 ease-in-out ${isCompleted ? 'bg-green-600' : 'bg-green-500'}`}
                style={{ width: isCompleted ? '100%' : `${progress}%` }}
              />
            </div>
          </div>
        </a>
        <CardFooter 
          className="flex justify-center items-center cursor-pointer"
          onClick={(e) => {
            if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
              e.preventDefault();
              const nextChapterPath = getNextChapterToLearn(course.id);
              navigate(nextChapterPath);
            }
          }}
        >
          <div className="text-sm text-muted-foreground">
            {isFree ? (
              <span className="bg-green-500 text-white px-1.5 py-0.5 rounded text-xs">
                GRATUIT
              </span>
            ) : hasPassCodeur ? (
              <span className="bg-green-500 text-white px-1.5 py-0.5 rounded text-xs">
                DISPONIBLE
              </span>
            ) : (
              <span className="bg-gradient-to-r from-emerald-600 to-blue-600 text-white px-1.5 py-0.5 rounded text-xs">
                NECESSITE LE PASS CODEUR
              </span>
            )}
          </div>
        </CardFooter>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Séparer les cours gratuits et payants
  const freeCourses = courses.filter(course => course.isFree);
  const paidCourses = courses.filter(course => !course.isFree);

  return (
    <div className="container mx-auto px-4 py-8">
      <SEO 
        title="Cours de programmation de jeux vidéo - Mon Premier Jeu"
        description="Découvrez tous les cours de programmation de jeux vidéo : Snake, Flappy Bird, Morpion et plus ! Formations gratuites et Pass Codeur disponibles."
        keywords="cours programmation, formation jeux vidéo, Snake, Flappy Bird, Morpion, p5.js, JavaScript, cours gratuits"
        url="/courses"
        type="website"
      />
      
      {courses.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">Aucune formation disponible pour le moment.</p>
        </div>
      ) : (
        <>
          {/* Section des cours gratuits */}
          {freeCourses.length > 0 && (
            <section className="mb-12">
              <h1 className="text-2xl font-bold mb-6 text-black dark:text-white">Les cours Gratuits</h1>
              <Separator />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                {freeCourses.map(course => renderCourseCard(course))}
              </div>
            </section>
          )}

          {/* Section des cours du Pass Codeur */}
          {paidCourses.length > 0 && (
            <section className="mb-12">
              <h1 className="text-2xl font-bold mb-6 text-black dark:text-white">Les cours du Pass Codeur</h1>
              <Separator />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                {paidCourses.map(course => renderCourseCard(course))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default Courses; 